import { useCallback } from 'react';

import { createPasskey } from '../store/actions/auth2Fa.actions';

import { useAuth2FaContext, STEPS } from '@/features/2fa/context/Auth2FaContext';
import { IAuth2FaCreationModalProps } from '@/features/2fa/components/Auth2FaCreationModal';
import { openModal } from '@/store/actions/core.actions';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-helpers';
import { IPasskeyParams } from '@/store/models/auth.model';
import { use2FaConfig } from '@/features/2fa/hooks/use2FaConfig';
import { setShouldChangePassword } from '@/features/dashboard-users/store/support-user.actions';

export function useCreatePasskey() {
  const { setCurrentStep } = useAuth2FaContext();
  const dispatch = useAppDispatch();

  const isLoading = useAppSelector((state) => state.auth.isLoading);

  const { scaApi, user, supportUserId, hints } = use2FaConfig();

  const onSubmitPasskey = useCallback(
    (newPasskey: IPasskeyParams) => {
      dispatch(
        createPasskey(newPasskey, (status) => {
          setCurrentStep(status === 'success' ? STEPS.REGISTER_SUCCESS : STEPS.REGISTER_FAIL);
          if (status === 'success') dispatch(setShouldChangePassword(true));
        }),
      );
    },
    [dispatch, setCurrentStep],
  );

  const register2Fa = useCallback(async () => {
    const challenge = JSON.stringify({
      iat: Date.now(),
    });

    const credential = await scaApi.create({
      type: 'webauthn',
      options: {
        challenge,
        user,
        extensions: {
          uvm: true,
          hmacCreateSecret: true,
          credProps: true,
        },
        hints,
      },
    });

    if (typeof credential !== 'string' && credential?.webauthn) {
      const newPasskey = { supportUserId, authenticator: credential.webauthn };

      dispatch(
        openModal<IAuth2FaCreationModalProps>('auth2FaCreationModal', {
          onSubmit: onSubmitPasskey,
          initialData: newPasskey,
        }),
      );
    }
  }, [scaApi, user, supportUserId, dispatch, onSubmitPasskey, hints]);

  return { register2Fa, isLoading };
}
